// Hooks del chat privado (agregado 15/10/2026). Ver src/firebase-chat.ts.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db, Task } from "../firebase-config";
import {
  ChatMessage,
  ChatSummary,
  GetPublicProfiles,
  getBestSkillArea,
  isChatUnread,
  MESSAGES_PAGE_SIZE,
  normalizeChat,
  normalizeMessage,
  PublicProfile,
  UpsertMyPublicProfile,
} from "../firebase-chat";
import { getLevelFromExp } from "../utils/levels";
import { isAdminEmail } from "../utils/adminAuth";

// ══════════════════════════════════════════════════════════
// Mis conversaciones (tiempo real). También alimenta el globito
// de "no leídos" del Sidebar.
// ══════════════════════════════════════════════════════════
export const useMyChats = (myUid?: string | null) => {
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!myUid) {
      setChats([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    // Sin orderBy a propósito: array-contains + orderBy pide un índice
    // compuesto. Ordenamos en el cliente, son pocas conversaciones.
    const q = query(
      collection(db, "Chats"),
      where("Miembros", "array-contains", myUid),
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map(normalizeChat)
          .sort(
            (a, b) =>
              (b.ActualizadoEl?.getTime() ?? 0) -
              (a.ActualizadoEl?.getTime() ?? 0),
          );
        setChats(list);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error escuchando chats:", error);
        setIsLoading(false);
      },
    );
    return () => unsubscribe();
  }, [myUid]);

  const unreadCount = useMemo(
    () => (myUid ? chats.filter((c) => isChatUnread(c, myUid)).length : 0),
    [chats, myUid],
  );

  return { chats, unreadCount, isLoading };
};

// ══════════════════════════════════════════════════════════
// Mensajes de una conversación: los últimos 50, y "ver anteriores"
// amplía el límite de a 50.
// ══════════════════════════════════════════════════════════
export const useChatMessages = (chatId?: string | null) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pageLimit, setPageLimit] = useState(MESSAGES_PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Al cambiar de conversación se vuelve al primer bloque.
  useEffect(() => {
    setPageLimit(MESSAGES_PAGE_SIZE);
    setMessages([]);
  }, [chatId]);

  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      setHasMore(false);
      return;
    }
    setIsLoading(true);
    const q = query(
      collection(db, "Chats", chatId, "Mensajes"),
      orderBy("Fecha", "desc"),
      limit(pageLimit),
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        // Vienen del más nuevo al más viejo; los damos vuelta para dibujar.
        setMessages(snap.docs.map(normalizeMessage).reverse());
        setHasMore(snap.size >= pageLimit);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error escuchando mensajes:", error);
        setIsLoading(false);
      },
    );
    return () => unsubscribe();
  }, [chatId, pageLimit]);

  const loadOlder = useCallback(
    () => setPageLimit((prev) => prev + MESSAGES_PAGE_SIZE),
    [],
  );

  return { messages, hasMore, isLoading, loadOlder };
};

// ══════════════════════════════════════════════════════════
// Perfiles públicos: una sola lectura por sesión (no es un listener)
// para gastar pocas lecturas del plan gratuito.
// ══════════════════════════════════════════════════════════
let profilesCache: PublicProfile[] | null = null;
let profilesRequest: Promise<PublicProfile[]> | null = null;

export const usePublicProfiles = () => {
  const [profiles, setProfiles] = useState<PublicProfile[]>(
    profilesCache ?? [],
  );
  const [isLoading, setIsLoading] = useState(!profilesCache);

  const load = useCallback(async (force = false) => {
    if (profilesCache && !force) {
      setProfiles(profilesCache);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      if (!profilesRequest || force) profilesRequest = GetPublicProfiles();
      profilesCache = await profilesRequest;
      setProfiles(profilesCache);
    } catch (error) {
      console.error("Error cargando perfiles públicos:", error);
    } finally {
      profilesRequest = null;
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const byId = useMemo(() => {
    const map = new Map<string, PublicProfile>();
    profiles.forEach((p) => map.set(p.id, p));
    return map;
  }, [profiles]);

  return { profiles, byId, isLoading, refresh: () => load(true) };
};

// ══════════════════════════════════════════════════════════
// Mantiene actualizado MI perfil público. Escribe solo cuando cambia
// nombre, avatar o nivel (se recuerda la última versión en localStorage).
// Se usa desde el Sidebar, que está en todas las páginas.
// ══════════════════════════════════════════════════════════
export const usePublicProfileSync = (userData: Task | null) => {
  useEffect(() => {
    if (!userData?.id) return;
    // La cuenta admin no aparece en Comunidad ni en el buscador del chat.
    if (isAdminEmail(userData.Email)) return;
    const img = userData.PortraitImg || "";
    const signature = [
      userData.Nombre,
      getLevelFromExp(userData.CurrentExp ?? 0),
      getBestSkillArea(userData.Skill ?? []),
      img.length,
      img.slice(-32),
    ].join("|");
    const key = `perfil-publico:${userData.id}`;
    try {
      if (localStorage.getItem(key) === signature) return;
    } catch {
      // Sin localStorage seguimos igual: solo escribimos de más.
    }
    UpsertMyPublicProfile(userData)
      .then(() => {
        try {
          localStorage.setItem(key, signature);
        } catch {
          /* no crítico */
        }
      })
      .catch((error) =>
        console.error("No se pudo actualizar el perfil público:", error),
      );
  }, [userData]);
};
